import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog, X, ChevronRight } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

interface ProfileCompletionBannerProps {
  missingFields: string[];
}

const DISMISS_KEY = 'luvall-profile-banner-dismissed';

const TOTAL_FIELDS = 4;

const LABELS: Record<string, string> = {
  DOB: 'date of birth',
  full_name: 'full name',
  location: 'location',
  postal_code: 'postal code',
};

export const ProfileCompletionBanner: React.FC<ProfileCompletionBannerProps> = ({ missingFields }) => {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (hidden || missingFields.length === 0) return null;

  const completed = TOTAL_FIELDS - missingFields.length;
  const pct = Math.round((completed / TOTAL_FIELDS) * 100);

  const dismiss = () => {
    setHidden(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mx-4 mb-4 rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-primary/15 p-2">
          <UserCog className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Finish your profile ({pct}%)</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Add your {missingFields.map((f) => LABELS[f] ?? f).join(', ')} so we can suggest nearby players and fair pairings.
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTES.EDIT_PROFILE)}
            className="mt-3 flex min-h-[44px] items-center gap-0.5 text-sm font-medium text-primary"
          >
            Complete profile <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss profile reminder"
          className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
